import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { useAuth, downloadProtectedFile } from "@/lib/auth";
import { useCurrencies, currencySymbol } from "@/lib/currency";
import Modal from "@/components/Modal";
import { EntitySearchSelect } from "@/components/EntitySearchSelect";
import ContainerEmailModal from "@/components/ContainerEmailModal";
import {
  Plus, Edit2, Trash2, Search, Container as ContainerIcon, RefreshCw,
  Ship, MapPin, AlertTriangle, Loader2, Info, FileDown, Clock, Mail, X,
} from "lucide-react";
import { toast } from "sonner";

// Mirrors CONTAINER_STATUSES in backend/container_tracking.py, in the same
// order — the labels and colours live here because only the UI needs them.
const STATUS_META = {
  booked: { label: "Booked", bg: "#F9F8F6", accent: "#6B6965" },
  gate_in: { label: "Gate In", bg: "#F0F4F8", accent: "#1E3A5F" },
  loaded: { label: "Loaded", bg: "#F0F4F8", accent: "#1E3A5F" },
  in_transit: { label: "In Transit", bg: "#FFF0E6", accent: "#C85A17" },
  transshipment: { label: "Transshipment", bg: "#FFF0E6", accent: "#C85A17" },
  discharged: { label: "Discharged", bg: "#FAF3E0", accent: "#946200" },
  gate_out: { label: "Gate Out", bg: "#FAF3E0", accent: "#946200" },
  delivered: { label: "Delivered", bg: "#EAF0EA", accent: "#2F4F4F" },
  empty_returned: { label: "Empty Returned", bg: "#EAF0EA", accent: "#2F4F4F" },
  on_hold: { label: "On Hold", bg: "#F5EDED", accent: "#B91C1C" },
};
const statusMeta = (k) => STATUS_META[k] || STATUS_META.booked;

const EMPTY = {
  amount: "", currency: "USD", invoice_number: "", bl_number: "", container_number: "",
  // One shipment can move in several boxes. The form edits this list; the
  // single container_number above is always its first entry, which is what the
  // export, the email draft and every older record still read.
  container_numbers: [""],
  customer_id: "", customer_name: "", supplier_id: "", supplier_name: "",
  description_one: "", description_two: "",
  shipping_line: "", container_type: "", seal_number: "", vessel: "", voyage_no: "",
  port_of_loading: "", port_of_discharge: "", etd: "", eta: "", atd: "", ata: "",
  status: "booked", notes: "",
};

// ISO 6346: 4 letters + 7 digits. Advisory only — a partial reference is still
// accepted, the user just gets a nudge.
const CONTAINER_NO_RE = /^[A-Z]{4}\d{7}$/;
const normalizeContainerNo = (v) => (v || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();

// Every box on a record, falling back to the single field for records saved
// before the list existed.
const boxesOf = (c) =>
  (c?.container_numbers?.length ? c.container_numbers : [c?.container_number]).filter(Boolean);

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" }) : "—");
const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
// Sweep times are shown in the schedule's own timezone, not the viewer's.
// "Mondays 06:00 Asia/Dubai · next Mon, 21 Sep, 07:30" reads like a mistake
// from an IST browser; both halves have to be on the same clock.
const fmtInZone = (iso, tz) => {
  if (!iso) return "—";
  const opts = { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" };
  try {
    return new Date(iso).toLocaleString(undefined, { ...opts, timeZone: tz || undefined });
  } catch {
    return new Date(iso).toLocaleString(undefined, opts); // unknown IANA zone
  }
};

// Arrival windows. Mirrors ETA_WINDOWS / _eta_window_clause in backend/server.py
// — both compare "YYYY-MM-DD" strings, so the chips, the sheet and the API all
// agree on what "next 7 days" means. Status is deliberately not considered: the
// window says when a box is due, not whether it has been handed over.
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const ETA_WINDOWS = [
  { key: "all", label: "All" },
  { key: "next7", label: "Next 7 days" },
  { key: "next30", label: "Next 30 days" },
  { key: "past", label: "Past ETA" },
  { key: "none", label: "No ETA" },
];

const inEtaWindow = (eta, key) => {
  if (key === "all") return true;
  if (key === "none") return !eta;
  if (!eta) return false;
  const today = todayStr();
  if (key === "past") return eta < today;
  if (key === "next7") return eta >= today && eta <= plusDays(7);
  if (key === "next30") return eta >= today && eta <= plusDays(30);
  return true;
};

// "in 3 days" / "today" / "5 days ago", shown under the ETA so a list of dates
// reads as a schedule at a glance.
const etaCountdown = (eta) => {
  if (!eta) return null;
  const ms = new Date(eta + "T00:00:00") - new Date(todayStr() + "T00:00:00");
  const days = Math.round(ms / 86_400_000);
  if (days === 0) return { text: "today", overdue: false };
  if (days > 0) return { text: `in ${days} day${days === 1 ? "" : "s"}`, overdue: false };
  return { text: `${-days} day${days === -1 ? "" : "s"} ago`, overdue: true };
};

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

// Read-only. The weekly sweep lives in backend/tracking_schedule.py and is the
// only thing that may call the carrier API, since every lookup is billed — so
// this reports what happened and offers no way to make it happen again.
function AutoTrackingStatus({ job }) {
  if (!job?.enabled) return null;

  const r = job.last_result || {};
  const tz = job.timezone;
  const failed = Number(r.failed) || 0;
  const checked = Number(r.checked) || 0;
  const candidates = Number(r.candidates) || 0;

  let outcome;
  if (job.running) outcome = "running now…";
  else if (!job.last_run_at) outcome = "hasn’t run yet";
  else if (!candidates) outcome = `last run ${fmtInZone(job.last_run_at, tz)} — nothing to check`;
  else outcome = `last run ${fmtInZone(job.last_run_at, tz)} — ${checked} of ${candidates} checked` +
    `, ${Number(r.status_changes) || 0} updated${failed ? `, ${failed} failed` : ""}`;

  // Every container failing is a systemic problem — an expired key, an outage,
  // a bad deploy — not 79 individually broken records, so it reads differently.
  const allFailed = !job.running && candidates > 0 && checked === 0;
  const tone = allFailed ? "var(--danger)" : "var(--text-2)";

  return (
    <div
      className="flex items-center gap-2 flex-wrap mb-4"
      style={{ padding: "9px 14px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--text-2)" }}
      data-testid="auto-tracking-status"
    >
      <Clock size={13} style={{ flexShrink: 0 }} />
      <span>
        Carriers are polled automatically <strong style={{ color: "var(--text)" }}>{job.schedule}</strong>
        {job.next_run_at ? ` · next ${fmtInZone(job.next_run_at, tz)}` : ""}
      </span>
      <span style={{ opacity: 0.6 }}>·</span>
      <span style={{ color: tone, fontWeight: allFailed ? 600 : 400 }} data-testid="auto-tracking-last-run">
        {allFailed && <AlertTriangle size={12} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />}
        {outcome}
      </span>
      {r.detail && !job.running && (
        <span style={{ color: "var(--danger)" }} data-testid="auto-tracking-detail">— {r.detail}</span>
      )}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="card-ledger" style={{ padding: "14px 18px" }}>
      <div className="overline" style={{ fontSize: 9 }}>{label}</div>
      <div className="font-mono-data" style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color: accent || "var(--text)" }}>{value}</div>
    </div>
  );
}

export default function Containers() {
  const { isAdmin } = useAuth();
  const currencies = useCurrencies();

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ shipping_lines: [], container_types: [], statuses: [], tracking: {} });

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [etaWindow, setEtaWindow] = useState("all");
  const [sortBy, setSortBy] = useState("eta"); // "eta" | "created"
  const [selected, setSelected] = useState([]);

  const [modal, setModal] = useState(null); // 'form' | 'detail'
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [emailFor, setEmailFor] = useState(null); // container being emailed to its customer
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Finished containers drop off the main list a week after they finish.
  const [showArchived, setShowArchived] = useState(false);
  const [job, setJob] = useState(null);   // read-only weekly-sweep status

  // Refresh reloads the meta too, so the sweep status on the page is current
  // without a full page reload — the one Monday it changes, that matters.
  const load = () => Promise.all([
    api.get("/containers", { params: { archived: showArchived } }).then((r) => setItems(r.data)),
    api.get("/container-stats").then((r) => setStats(r.data)).catch(() => {}),
    api.get("/container-meta").then((r) => {
      setMeta(r.data);
      setJob(r.data.tracking_job || null);
    }).catch(() => {}),
  ]);

  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carrier tracking is entirely automatic (backend/tracking_schedule.py sweeps
  // every Monday). Nothing in the UI triggers a provider call — each lookup is
  // billed, so the schedule is the only thing allowed to spend the quota. This
  // flag just decides how to word an empty timeline.
  const trackingReady = !!meta.tracking?.configured;
  const symbolFor = (code) => currencySymbol(code, currencies);

  const openAdd = () => { setForm(EMPTY); setEditing(null); setModal("form"); };
  const openEdit = (c) => {
    const boxes = boxesOf(c);
    setForm({
      ...EMPTY, ...c,
      amount: c.amount === 0 ? "" : String(c.amount),
      container_numbers: boxes.length ? boxes : [""],  // always one row to type into
    });
    setEditing(c);
    setModal("form");
  };
  const openDetail = (c) => { setDetail(c); setModal("detail"); };
  // Modal is a single overlay, so the detail view steps aside for the composer.
  const openEmail = (c) => { setModal(null); setEmailFor(c); };

  // Box rows. The list never empties — removing the last row leaves a blank one
  // so there is always somewhere to type.
  const setBox = (i, v) => setForm((f) => ({
    ...f, container_numbers: f.container_numbers.map((b, j) => (j === i ? v : b)),
  }));
  const addBox = () => setForm((f) => ({ ...f, container_numbers: [...f.container_numbers, ""] }));
  const removeBox = (i) => setForm((f) => {
    const next = f.container_numbers.filter((_, j) => j !== i);
    return { ...f, container_numbers: next.length ? next : [""] };
  });

  const onCustomer = (c) => setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.company || "" }));
  const onSupplier = (s) => setForm((f) => ({ ...f, supplier_id: s.id, supplier_name: s.company || "" }));
  const clearCustomer = () => setForm((f) => ({ ...f, customer_id: "", customer_name: "" }));
  const clearSupplier = () => setForm((f) => ({ ...f, supplier_id: "", supplier_name: "" }));

  const save = async () => {
    if (saving) return;
    // Blank rows are dropped and a number typed twice is kept once, so an
    // abandoned row never becomes an empty box on the record.
    const boxes = [];
    for (const raw of form.container_numbers) {
      const box = normalizeContainerNo(raw);
      if (box && !boxes.includes(box)) boxes.push(box);
    }
    if (!boxes.length && !form.bl_number.trim() && !form.invoice_number.trim()) {
      toast.error("Enter at least a container number, BL number or invoice number");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount) || 0,
        container_numbers: boxes,
        container_number: boxes[0] || "",  // the server keeps these two in step
      };
      if (editing) await api.patch(`/containers/${editing.id}`, payload);
      else await api.post("/containers", payload);
      toast.success(editing ? "Container updated" : "Container added");
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this container entry?")) return;
    try {
      await api.delete(`/containers/${id}`);
      toast.success("Deleted");
      setSelected((s) => s.filter((x) => x !== id));
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} container${selected.length > 1 ? "s" : ""}?`)) return;
    try {
      await api.post("/containers/bulk-delete", { ids: selected });
      toast.success(`Deleted ${selected.length} container${selected.length > 1 ? "s" : ""}`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete containers");
    }
  };

  // Excel download. The server re-applies the same status/search filter, so the
  // sheet matches the table; an explicit selection narrows it further.
  const exportXlsx = async (onlySelected = false) => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (showArchived) params.set("archived", "true");
      if (onlySelected) params.set("ids", selected.join(","));
      else {
        if (filter !== "all") params.set("status", filter);
        if (search.trim()) params.set("search", search.trim());
        if (etaWindow !== "all") params.set("eta_window", etaWindow);
      }
      params.set("sort", sortBy);
      const qs = params.toString();
      await downloadProtectedFile(
        `/containers/export.xlsx${qs ? `?${qs}` : ""}`,
        `Containers_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not export containers");
    } finally {
      setExporting(false);
    }
  };

  // Status + search first; the arrival chips then narrow this set, so their
  // counts always match what clicking one actually shows.
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((c) => {
      if (filter !== "all" && c.status !== filter) return false;
      if (!q) return true;
      return [...boxesOf(c), c.bl_number, c.invoice_number, c.customer_name, c.supplier_name, c.vessel, c.port_of_loading, c.port_of_discharge, c.description_one, c.description_two]
        .some((v) => v?.toLowerCase().includes(q));
    });
  }, [items, search, filter]);

  const etaCounts = useMemo(() => {
    const counts = {};
    for (const w of ETA_WINDOWS) counts[w.key] = searched.filter((c) => inEtaWindow(c.eta, w.key)).length;
    return counts;
  }, [searched]);

  const filtered = useMemo(() => {
    const rows = searched.filter((c) => inEtaWindow(c.eta, etaWindow));
    if (sortBy !== "eta") return rows;
    // Soonest arrival first, containers with no ETA parked at the end.
    return [...rows].sort((a, b) => {
      if (!a.eta !== !b.eta) return a.eta ? -1 : 1;
      return (a.eta || "").localeCompare(b.eta || "");
    });
  }, [searched, etaWindow, sortBy]);

  const filteredIds = filtered.map((c) => c.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.includes(id));
  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleSelectFiltered = () => setSelected((s) =>
    allFilteredSelected ? s.filter((id) => !filteredIds.includes(id)) : [...new Set([...s, ...filteredIds])]
  );

  const statusOptions = meta.statuses?.length ? meta.statuses : Object.keys(STATUS_META);
  const lineOptions = meta.shipping_lines || [];
  const typeOptions = meta.container_types || [];

  const totalValue = useMemo(() => {
    const entries = Object.entries(stats?.value_by_currency || {}).filter(([, v]) => v);
    if (entries.length === 0) return "—";
    return entries.map(([code, v]) => fmtMoney(v, symbolFor(code))).join(" + ");
  }, [stats, currencies]); // eslint-disable-line react-hooks/exhaustive-deps

  // Advisory per row: a partial reference is a legitimate thing to record
  // before the line confirms the final box number.
  const boxWarning = (v) => v.trim() && !CONTAINER_NO_RE.test(normalizeContainerNo(v));

  return (
    <div data-testid="containers-page">
      {/* KPI strip */}
      <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }} data-testid="container-kpis">
        <Kpi label="Containers" value={stats?.total ?? "—"} />
        <Kpi label="In Transit" value={stats?.in_transit ?? "—"} accent="#C85A17" />
        <Kpi label="Delivered" value={stats?.delivered ?? "—"} accent="#2F4F4F" />
        <Kpi label="On Hold" value={stats?.on_hold ?? "—"} accent={stats?.on_hold ? "#B91C1C" : undefined} />
        <div className="card-ledger" style={{ padding: "14px 18px" }}>
          <div className="overline" style={{ fontSize: 9 }}>Total Invoiced</div>
          <div className="font-mono-data" style={{ fontSize: 16, fontWeight: 700, marginTop: 8, lineHeight: 1.3 }}>{totalValue}</div>
        </div>
      </div>

      {/* {!trackingReady && (
        <div className="flex items-start gap-2 mb-4" style={{ padding: "10px 14px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--text-2)" }} data-testid="tracking-not-configured">
          <Info size={14} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>
            Live carrier tracking isn’t connected yet — set <span className="font-mono-data">CONTAINER_TRACKING_PROVIDER</span> and{" "}
            <span className="font-mono-data">CONTAINER_TRACKING_API_KEY</span> in <span className="font-mono-data">backend/.env</span>.
            Until then, set each container’s status by hand.
          </span>
        </div>
      )} */}

      {trackingReady && <AutoTrackingStatus job={job} />}

      {isAdmin && selected.length > 0 && (
        <div className="flex items-center gap-3 mb-4" style={{ padding: "10px 14px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8 }} data-testid="bulk-actions">
          <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.length} selected</span>
          <button onClick={() => setSelected([])} className="btn-ghost" style={{ fontSize: 12 }}>Clear</button>
          <button onClick={() => exportXlsx(true)} disabled={exporting} className="btn-outline ml-auto" data-testid="bulk-export-btn">
            {exporting ? <Loader2 size={14} className="spin" /> : <FileDown size={14} />} Export selected
          </button>
          <button onClick={onBulkDelete} className="btn-outline" style={{ color: "var(--danger)", borderColor: "var(--danger)" }} data-testid="bulk-delete-btn">
            <Trash2 size={14} /> Delete selected
          </button>
        </div>
      )}

      {/* Search + add */}
      <div className="flex flex-wrap gap-3 items-center mb-3">
        <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 420 }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-2)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search container, BL, invoice, customer…" className="input-base" style={{ paddingLeft: 36 }} data-testid="containers-search" />
        </div>
        <button onClick={load} className="btn-ghost" data-testid="containers-reload"><RefreshCw size={14} /> Refresh</button>
        <button
          onClick={() => exportXlsx(false)}
          disabled={exporting || items.length === 0}
          className="btn-outline"
          title="Download the filtered list as an Excel file"
          data-testid="containers-export-btn"
        >
          {exporting ? <Loader2 size={14} className="spin" /> : <FileDown size={14} />} Export Excel
        </button>
        <button onClick={openAdd} className="btn-primary ml-auto" data-testid="add-container-btn"><Plus size={15} /> Add Container</button>
      </div>

      {/* Status filters */}
      <div className="flex gap-2 flex-wrap mb-4" data-testid="container-filters">
        {["all", ...statusOptions].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={filter === k ? "btn-primary" : "btn-outline"}
            style={{ padding: "6px 12px", fontSize: 12 }}
            data-testid={`container-filter-${k}`}
          >
            {k === "all" ? "All" : statusMeta(k).label}
            {k !== "all" && stats?.by_status?.[k] ? ` (${stats.by_status[k]})` : ""}
          </button>
        ))}
        <button
          onClick={() => { setShowArchived((v) => !v); setSelected([]); }}
          className={showArchived ? "btn-primary" : "btn-outline"}
          style={{ padding: "6px 12px", fontSize: 12 }}
          title="Delivered or returned more than a week ago"
          data-testid="container-filter-archived"
        >
          Archived{stats?.archived ? ` (${stats.archived})` : ""}
        </button>
      </div>

      {/* Arrivals window + sort */}
      <div className="flex gap-2 flex-wrap items-center mb-4" data-testid="container-eta-filters">
        <span className="overline" style={{ fontSize: 9, marginRight: 2 }}>Arrivals</span>
        {ETA_WINDOWS.map((w) => (
          <button
            key={w.key}
            onClick={() => setEtaWindow(w.key)}
            className={etaWindow === w.key ? "btn-primary" : "btn-outline"}
            style={{ padding: "6px 12px", fontSize: 12 }}
            data-testid={`container-eta-${w.key}`}
          >
            {w.label}
            {w.key !== "all" && etaCounts[w.key] ? ` (${etaCounts[w.key]})` : ""}
          </button>
        ))}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="input-base ml-auto"
          style={{ width: "auto", padding: "6px 10px", fontSize: 12 }}
          data-testid="container-sort"
        >
          <option value="eta">ETA — soonest first</option>
          <option value="created">Recently added</option>
        </select>
      </div>

      {items.length === 0 ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }} data-testid="containers-empty">
          <ContainerIcon size={28} style={{ margin: "0 auto 12px" }} />
          <div style={{ fontSize: 14 }}>No containers tracked yet.</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }} data-testid="containers-no-match">
          <ContainerIcon size={28} style={{ margin: "0 auto 12px" }} />
          <div style={{ fontSize: 14 }}>
            {etaWindow === "all"
              ? "No containers match this filter."
              : `No containers in the “${ETA_WINDOWS.find((w) => w.key === etaWindow)?.label}” window.`}
          </div>
        </div>
      ) : (
        <div className="card-ledger" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1240 }}>
            <thead><tr style={{ background: "var(--surface-2)" }}>
              {isAdmin && (
                <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "1px solid var(--border)", width: 40 }}>
                  <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectFiltered} title="Select all visible" data-testid="select-all-checkbox" style={{ cursor: "pointer" }} />
                </th>
              )}
              {["Customer", "Container #", "ETD", "ETA", "Status", "BL #", "Invoice #", "Description 1", "Description 2", "Route", "Amount", ""].map((h, i) => (
                <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 16px", textAlign: h === "Amount" ? "right" : "left", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.map((c) => {
                const m = statusMeta(c.status);
                return (
                  <tr key={c.id} style={{ borderBottom: "1px solid var(--border)" }} data-testid={`container-row-${c.id}`}>
                    {isAdmin && (
                      <td style={{ padding: "14px 16px" }}>
                        <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} data-testid={`select-checkbox-${c.id}`} style={{ cursor: "pointer" }} />
                      </td>
                    )}
                    <td style={{ padding: "14px 16px", fontSize: 13, fontWeight: 700 }}>
                      {c.customer_name || "—"}
                      {c.supplier_name && <div style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 400 }}>from {c.supplier_name}</div>}
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openDetail(c)}
                          className="font-mono-data"
                          style={{ fontWeight: 700, fontSize: 13, background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--primary)" }}
                          data-testid={`container-open-${c.id}`}
                        >
                          {boxesOf(c)[0] || "—"}
                        </button>
                        {/* The row stays one line however many boxes there are;
                            the rest are on the detail panel and in the tooltip. */}
                        {boxesOf(c).length > 1 && (
                          <button
                            onClick={() => openDetail(c)}
                            className="tag"
                            title={boxesOf(c).join(", ")}
                            style={{ fontSize: 10, cursor: "pointer", background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text-2)" }}
                            data-testid={`container-box-count-${c.id}`}
                          >
                            +{boxesOf(c).length - 1}
                          </button>
                        )}
                      </div>
                      {c.container_type && <div style={{ fontSize: 11, color: "var(--text-2)" }}>{c.container_type}</div>}
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 13, whiteSpace: "nowrap" }} className="font-mono-data">{fmtDate(c.etd)}</td>
                    <td style={{ padding: "14px 16px", fontSize: 13, whiteSpace: "nowrap" }} data-testid={`container-eta-cell-${c.id}`}>
                      <span className="font-mono-data">{fmtDate(c.eta)}</span>
                      {(() => {
                        const cd = etaCountdown(c.eta);
                        return cd ? (
                          <div style={{ fontSize: 11, color: cd.overdue ? "var(--danger)" : "var(--text-2)" }}>{cd.text}</div>
                        ) : null;
                      })()}
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <span className="tag" style={{ background: m.bg, borderColor: "var(--border)", color: m.accent, whiteSpace: "nowrap" }} data-testid={`container-status-${c.id}`}>{m.label}</span>
                      {c.tracking_error && (
                        <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                          <AlertTriangle size={11} /> tracking failed
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 13 }} className="font-mono-data">{c.bl_number || "—"}</td>
                    <td style={{ padding: "14px 16px", fontSize: 13 }} className="font-mono-data">{c.invoice_number || "—"}</td>
                    <td style={{ padding: "14px 16px", fontSize: 13, maxWidth: 220 }} title={c.description_one || ""}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.description_one || "—"}</div>
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 13, maxWidth: 220 }} title={c.description_two || ""}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.description_two || "—"}</div>
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 12, color: "var(--text-2)", whiteSpace: "nowrap" }}>
                      {c.port_of_loading || c.port_of_discharge ? `${c.port_of_loading || "?"} → ${c.port_of_discharge || "?"}` : "—"}
                      {c.shipping_line && <div style={{ fontSize: 11 }}>{c.shipping_line}</div>}
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 13, textAlign: "right", whiteSpace: "nowrap" }} className="font-mono-data">
                      {c.amount ? fmtMoney(c.amount, symbolFor(c.currency)) : "—"}
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "right" }}>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEmail(c)} className="btn-ghost" title="Email customer the invoice, BL and ETA" data-testid={`container-email-${c.id}`}><Mail size={14} /></button>
                        <button onClick={() => openEdit(c)} className="btn-ghost" title="Edit" data-testid={`container-edit-${c.id}`}><Edit2 size={14} /></button>
                        <button onClick={() => remove(c.id)} className="btn-ghost" style={{ color: "var(--danger)" }} title="Delete" data-testid={`container-delete-${c.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal === "form" && (
        <Modal
          title={editing ? "Edit Container" : "Add Container"}
          subtitle="Commercial invoice, BL and box details for one shipment"
          onClose={() => setModal(null)}
          size="lg"
        >
          <div className="grid gap-5">
            <div>
              <div className="overline" style={{ fontSize: 9, marginBottom: 10 }}>Commercial</div>
              <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                <Field label="Amount">
                  <input
                    type="number" min="0" step="0.01" value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="input-base font-mono-data no-spin" placeholder="0.00" data-testid="container-amount"
                  />
                </Field>
                <Field label="Currency">
                  <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="input-base" data-testid="container-currency">
                    {(currencies.length ? currencies : [{ code: "USD", label: "US Dollar" }]).map((c) => (
                      <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Invoice Number">
                  <input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="input-base font-mono-data" data-testid="container-invoice" />
                </Field>
              </div>
              <div className="grid gap-4 mt-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <Field label="Customer">
                  <EntitySearchSelect
                    endpoint="/customers"
                    value={form.customer_id}
                    label={form.customer_name}
                    onSelect={onCustomer}
                    onClear={clearCustomer}
                    placeholder="Search customers…"
                    testId="container-customer"
                  />
                </Field>
                <Field label="Supplier">
                  <EntitySearchSelect
                    endpoint="/suppliers"
                    value={form.supplier_id}
                    label={form.supplier_name}
                    onSelect={onSupplier}
                    onClear={clearSupplier}
                    placeholder="Search suppliers…"
                    testId="container-supplier"
                  />
                </Field>
              </div>
              <div className="grid gap-4 mt-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <Field label="Description 1">
                  <input
                    value={form.description_one}
                    onChange={(e) => setForm({ ...form, description_one: e.target.value })}
                    placeholder="Goods as described by the supplier"
                    className="input-base" data-testid="container-description-one"
                  />
                </Field>
                <Field label="Description 2">
                  <input
                    value={form.description_two}
                    onChange={(e) => setForm({ ...form, description_two: e.target.value })}
                    placeholder="Goods as invoiced to the customer"
                    className="input-base" data-testid="container-description-two"
                  />
                </Field>
              </div>
            </div>

            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 18 }}>
              <div className="overline" style={{ fontSize: 9, marginBottom: 10 }}>Shipment</div>
              <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <Field label="BL Number">
                  <input value={form.bl_number} onChange={(e) => setForm({ ...form, bl_number: e.target.value })} className="input-base font-mono-data" data-testid="container-bl" />
                </Field>
                <Field label="Seal Number">
                  <input value={form.seal_number} onChange={(e) => setForm({ ...form, seal_number: e.target.value })} className="input-base font-mono-data" />
                </Field>
              </div>

              {/* One booking, several boxes: each row is one container number
                  on this same shipment. The first row is the record's
                  container number everywhere else in the app. */}
              <div className="mt-4">
                <Field
                  label="Container Numbers"
                  hint="One box per row — a shipment that moves in several containers stays one record."
                >
                  <div className="grid gap-2" data-testid="container-number-rows">
                    {form.container_numbers.map((value, i) => (
                      <div key={i}>
                        <div className="flex gap-2 items-center">
                          <span className="font-mono-data" style={{ fontSize: 11, color: "var(--text-2)", width: 16, flexShrink: 0, textAlign: "right" }}>{i + 1}</span>
                          <input
                            value={value}
                            onChange={(e) => setBox(i, e.target.value.toUpperCase())}
                            placeholder={i === 0 ? "MSDU1234567" : "Another container on this shipment"}
                            className="input-base font-mono-data"
                            data-testid={i === 0 ? "container-number" : `container-number-${i}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeBox(i)}
                            className="btn-ghost"
                            title="Remove this container"
                            disabled={form.container_numbers.length === 1 && !value}
                            style={{ color: "var(--danger)", flexShrink: 0, padding: "6px 8px" }}
                            data-testid={`container-number-remove-${i}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                        {boxWarning(value) && (
                          <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 4, marginLeft: 24 }}>
                            Doesn’t look like an ISO 6346 number (4 letters + 7 digits) — saved as entered.
                          </div>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addBox}
                      className="btn-outline"
                      style={{ justifySelf: "start", padding: "6px 12px", fontSize: 12, marginLeft: 24 }}
                      data-testid="container-number-add"
                    >
                      <Plus size={13} /> Add another container
                    </button>
                  </div>
                </Field>
              </div>

              <div className="grid gap-4 mt-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <Field label="Shipping Line">
                  <select value={form.shipping_line} onChange={(e) => setForm({ ...form, shipping_line: e.target.value })} className="input-base" data-testid="container-line">
                    <option value="">— Select —</option>
                    {lineOptions.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Container Type">
                  <select value={form.container_type} onChange={(e) => setForm({ ...form, container_type: e.target.value })} className="input-base" data-testid="container-type">
                    <option value="">— Select —</option>
                    {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid gap-4 mt-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <Field label="Vessel">
                  <input value={form.vessel} onChange={(e) => setForm({ ...form, vessel: e.target.value })} className="input-base" />
                </Field>
                <Field label="Voyage No">
                  <input value={form.voyage_no} onChange={(e) => setForm({ ...form, voyage_no: e.target.value })} className="input-base font-mono-data" />
                </Field>
              </div>
              <div className="grid gap-4 mt-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <Field label="Port of Loading">
                  <input value={form.port_of_loading} onChange={(e) => setForm({ ...form, port_of_loading: e.target.value })} placeholder="Nhava Sheva, IN" className="input-base" />
                </Field>
                <Field label="Port of Discharge">
                  <input value={form.port_of_discharge} onChange={(e) => setForm({ ...form, port_of_discharge: e.target.value })} placeholder="Jebel Ali, AE" className="input-base" />
                </Field>
              </div>
            </div>

            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 18 }}>
              <div className="overline" style={{ fontSize: 9, marginBottom: 10 }}>Schedule &amp; Status</div>
              <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}>
                <Field label="ETD"><input type="date" value={form.etd} onChange={(e) => setForm({ ...form, etd: e.target.value })} className="input-base font-mono-data" /></Field>
                <Field label="ATD"><input type="date" value={form.atd} onChange={(e) => setForm({ ...form, atd: e.target.value })} className="input-base font-mono-data" /></Field>
                <Field label="ETA"><input type="date" value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} className="input-base font-mono-data" data-testid="container-eta" /></Field>
                <Field label="ATA"><input type="date" value={form.ata} onChange={(e) => setForm({ ...form, ata: e.target.value })} className="input-base font-mono-data" /></Field>
              </div>
              <div className="grid gap-4 mt-4" style={{ gridTemplateColumns: "1fr 2fr" }}>
                <Field label="Status">
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-base" data-testid="container-status">
                    {statusOptions.map((s) => <option key={s} value={s}>{statusMeta(s).label}</option>)}
                  </select>
                </Field>
                <Field label="Notes">
                  <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-base" />
                </Field>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button onClick={() => setModal(null)} className="btn-outline" disabled={saving}>Cancel</button>
              <button onClick={save} className="btn-primary" disabled={saving} data-testid="container-save">
                {saving ? <Loader2 size={14} className="spin" /> : null} Save
              </button>
            </div>
          </div>
        </Modal>
      )}

      {modal === "detail" && detail && (
        <Modal
          title={boxesOf(detail)[0] || detail.bl_number || "Container"}
          subtitle={[
            boxesOf(detail).length > 1 ? `${boxesOf(detail).length} containers` : "",
            detail.shipping_line,
            detail.container_type,
          ].filter(Boolean).join(" · ")}
          onClose={() => setModal(null)}
          size="lg"
        >
          <div className="grid gap-5">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="tag" style={{ background: statusMeta(detail.status).bg, borderColor: "var(--border)", color: statusMeta(detail.status).accent }}>
                {statusMeta(detail.status).label}
              </span>
              {detail.tracking_location && (
                <span style={{ fontSize: 12, color: "var(--text-2)", display: "flex", alignItems: "center", gap: 4 }}>
                  <MapPin size={12} /> {detail.tracking_location}
                </span>
              )}
            </div>

            {boxesOf(detail).length > 0 && (
              <div data-testid="container-detail-boxes">
                <div className="overline" style={{ fontSize: 9, marginBottom: 6 }}>
                  Container Numbers ({boxesOf(detail).length})
                </div>
                <div className="grid gap-2">
                  {boxesOf(detail).map((box) => {
                    // Each box is polled on its own, so it carries its own
                    // milestone; the record's status is the least advanced of them.
                    const t = (detail.tracking_boxes || []).find((b) => b.container_number === box);
                    const bm = t?.status ? statusMeta(t.status) : null;
                    return (
                      <div
                        key={box}
                        className="flex items-center gap-3 flex-wrap"
                        style={{ padding: "8px 10px", background: "var(--surface-2)", borderRadius: 6 }}
                        data-testid={`container-detail-box-${box}`}
                      >
                        <span className="font-mono-data" style={{ fontSize: 13, fontWeight: 700 }}>{box}</span>
                        {bm && (
                          <span className="tag" style={{ background: bm.bg, borderColor: "var(--border)", color: bm.accent, fontSize: 10 }}>
                            {bm.label}
                          </span>
                        )}
                        {t?.location && (
                          <span style={{ fontSize: 11, color: "var(--text-2)", display: "flex", alignItems: "center", gap: 4 }}>
                            <MapPin size={11} /> {t.location}
                          </span>
                        )}
                        {t?.error && (
                          <span style={{ fontSize: 11, color: "var(--danger)", display: "flex", alignItems: "center", gap: 4 }}>
                            <AlertTriangle size={11} /> {t.error}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
              {[
                ["Amount", detail.amount ? fmtMoney(detail.amount, symbolFor(detail.currency)) : "—"],
                ["Invoice #", detail.invoice_number || "—"],
                ["BL #", detail.bl_number || "—"],
                ["Customer", detail.customer_name || "—"],
                ["Supplier", detail.supplier_name || "—"],
                ["Description 1 (purchase)", detail.description_one || "—"],
                ["Description 2 (sales)", detail.description_two || "—"],
                ["Vessel / Voyage", [detail.vessel, detail.voyage_no].filter(Boolean).join(" / ") || "—"],
                ["Seal #", detail.seal_number || "—"],
                ["POL → POD", detail.port_of_loading || detail.port_of_discharge ? `${detail.port_of_loading || "?"} → ${detail.port_of_discharge || "?"}` : "—"],
                ["ETD / ATD", `${fmtDate(detail.etd)} / ${fmtDate(detail.atd)}`],
                ["ETA / ATA", `${fmtDate(detail.eta)} / ${fmtDate(detail.ata)}`],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="overline" style={{ fontSize: 9 }}>{label}</div>
                  <div className="font-mono-data" style={{ fontSize: 13, marginTop: 3 }}>{value}</div>
                </div>
              ))}
            </div>

            {detail.notes && (
              <div>
                <div className="overline" style={{ fontSize: 9, marginBottom: 4 }}>Notes</div>
                <div style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "pre-wrap" }}>{detail.notes}</div>
              </div>
            )}

            {detail.tracking_error && (
              <div style={{ fontSize: 12, color: "var(--danger)", padding: "8px 10px", background: "#FCE8E8", borderRadius: 4 }}>
                <AlertTriangle size={12} style={{ display: "inline", marginRight: 4 }} /> {detail.tracking_error}
              </div>
            )}

            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
              <div className="flex items-center justify-between mb-3">
                <div className="overline" style={{ fontSize: 9 }}>Tracking Timeline</div>
                <div style={{ fontSize: 11, color: "var(--text-2)" }}>
                  {detail.tracking_updated_at ? `Last checked ${fmtStamp(detail.tracking_updated_at)}` : "Never checked"}
                </div>
              </div>
              {detail.tracking_events?.length ? (
                <div className="grid gap-2">
                  {detail.tracking_events.map((ev, i) => (
                    <div key={i} className="flex gap-3" style={{ padding: "10px 12px", background: "var(--surface-2)", borderRadius: 6 }}>
                      <Ship size={14} style={{ marginTop: 2, flexShrink: 0, color: "var(--text-2)" }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{ev.description || ev.status || "Event"}</div>
                        <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 2 }}>
                          {[
                            // Only worth naming when the record has more than one.
                            boxesOf(detail).length > 1 ? ev.container_number : "",
                            ev.date,
                            ev.location,
                          ].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "var(--text-2)" }}>
                  {trackingReady
                    ? "No tracking events yet. Carriers are polled automatically every Monday morning."
                    : "Carrier tracking isn’t connected yet — status is set manually."}
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end" style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
              <button onClick={() => remove(detail.id)} className="btn-outline" style={{ color: "var(--danger)", borderColor: "var(--danger)" }}>
                <Trash2 size={14} /> Delete
              </button>
              <button onClick={() => openEmail(detail)} className="btn-outline" data-testid="container-detail-email"><Mail size={14} /> Email Customer</button>
              <button onClick={() => { setModal(null); openEdit(detail); }} className="btn-primary"><Edit2 size={14} /> Edit</button>
            </div>
          </div>
        </Modal>
      )}

      {emailFor && (
        <ContainerEmailModal container={emailFor} onClose={() => setEmailFor(null)} />
      )}
    </div>
  );
}
